import { ReactNode, useEffect, useMemo, useState } from 'react';
import type { ServiceEntry, SidebarGroup, SidebarItem } from '../services/types';
import { TopBar } from './TopBar';
import { Sidebar } from './Sidebar';
import { MobileDrawer } from './MobileDrawer';
import { PageActionsProvider, usePageActionsSlot } from './PageActions';
import { BypassBanner } from '../widgets/BypassBanner';
import { BuildBadge } from '../widgets/BuildBadge';
import { UserMenu } from '../widgets/UserMenu';
import { ChevronLeftIcon, ChevronRightIcon } from '../icons';

interface AppShellProps {
  appName: string;
  services: ServiceEntry[];
  activeServiceId?: string | null;
  sidebarItems?: SidebarItem[];
  sidebarGroups?: SidebarGroup[];
  activeSidebarPath?: string;
  homeUrl?: string;
  /** Explicit breadcrumb label. Overrides sidebar-derived label. Pass null to hide. */
  currentPageLabel?: string | null;
  /**
   * Content rendered on the TopBar's right side (before nothing else — the
   * right is otherwise empty now). Subsystems use this for page-scoped
   * actions like Filter, Export, New, etc.
   */
  topBarActions?: ReactNode;
  /**
   * URL for the subsystem backend's `/version` endpoint. When set,
   * `<BuildBadge>` in the sidebar footer renders a BE line alongside the FE
   * line, surfacing "BE offline" when the fetch fails. Convention:
   * `/api/<id>/version` (e.g. `/api/monitoring/version`). Omit for shell.
   */
  backendVersionUrl?: string;
  children: ReactNode;
}

const COLLAPSE_KEY = 'ops-hub:sidebar-collapsed';

/**
 * Layout shell:
 *   ┌─ BypassBanner (dev only) ───────────────────────────────┐
 *   ├─ TopBar: logo · service-dropdown · breadcrumb ··· actions
 *   └─ Main row:
 *        Sidebar (lg+)    │    Outlet (children)
 *          · nav items
 *          · collapse toggle
 *          · UserMenu (bottom)
 */
export function AppShell(props: AppShellProps) {
  // PageActionsProvider must wrap AppShellInner so the latter can read
  // whatever the currently-mounted page registered via <PageActions>.
  return (
    <PageActionsProvider>
      <AppShellInner {...props} />
    </PageActionsProvider>
  );
}

function AppShellInner({
  appName,
  services,
  activeServiceId,
  sidebarItems,
  sidebarGroups,
  activeSidebarPath,
  homeUrl = '/',
  currentPageLabel,
  topBarActions,
  backendVersionUrl,
  children,
}: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSE_KEY) === 'true';
  });
  const setCollapsed = (v: boolean) => {
    setCollapsedState(v);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(COLLAPSE_KEY, v ? 'true' : 'false');
    }
  };

  const hasSidebar = Boolean(sidebarItems?.length || sidebarGroups?.length);

  const derivedPageLabel = useMemo(() => {
    if (currentPageLabel !== undefined) return currentPageLabel;
    if (!activeSidebarPath) return null;
    return findSidebarLabel(activeSidebarPath, sidebarItems, sidebarGroups);
  }, [currentPageLabel, activeSidebarPath, sidebarItems, sidebarGroups]);

  // Pages can register actions via <PageActions> — that takes precedence
  // over any static topBarActions passed directly to AppShell.
  const registeredActions = usePageActionsSlot();
  const finalActions = registeredActions ?? topBarActions;

  // Keep the CSS custom property fresh for any consumer styling
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.style.setProperty(
      '--ops-sidebar-width',
      collapsed ? '3.5rem' : '14rem',
    );
  }, [collapsed]);

  return (
    <div className="h-dvh overflow-hidden flex flex-col bg-background">
      <BypassBanner />

      <TopBar
        appName={appName}
        homeUrl={homeUrl}
        services={services}
        activeServiceId={activeServiceId}
        currentPageLabel={derivedPageLabel}
        actions={finalActions}
        onToggleDrawer={() => setDrawerOpen(true)}
      />

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {hasSidebar && (
          <aside
            className={
              'hidden lg:flex flex-col shrink-0 border-r bg-background transition-[width] duration-150 ' +
              (collapsed ? 'w-14' : 'w-56')
            }
          >
            <div
              className={
                'flex-1 min-h-0 ' +
                (collapsed ? 'overflow-visible' : 'overflow-y-auto')
              }
            >
              <Sidebar
                items={sidebarItems}
                groups={sidebarGroups}
                activePath={activeSidebarPath}
                collapsed={collapsed}
              />
            </div>

            {/* Footer: user menu + collapse toggle.
                Padding matches the main Sidebar (px-3 expanded, px-2 collapsed)
                so the user-address text aligns with sidebar item labels. */}
            <div
              className={
                'border-t py-2 flex flex-col gap-0.5 ' +
                (collapsed ? 'items-center px-2' : 'px-3')
              }
            >
              <UserMenu compact={collapsed} placement="top" />
              <button
                type="button"
                onClick={() => setCollapsed(!collapsed)}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                className={
                  'inline-flex items-center gap-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors ' +
                  (collapsed ? 'justify-center w-10 h-8' : 'justify-start px-2.5 py-1.5 w-full')
                }
              >
                {collapsed ? (
                  <ChevronRightIcon className="w-4 h-4" />
                ) : (
                  <>
                    <span className="w-4 h-4 shrink-0 flex items-center justify-center">
                      <ChevronLeftIcon className="w-4 h-4" />
                    </span>
                    <span>Collapse</span>
                  </>
                )}
              </button>
              <div className={'pt-1 ' + (collapsed ? 'flex justify-center' : 'px-1')}>
                <BuildBadge backendVersionUrl={backendVersionUrl} compact={collapsed} />
              </div>
            </div>
          </aside>
        )}

        <main className="flex-1 min-w-0 overflow-auto">{children}</main>
      </div>

      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sidebarItems={sidebarItems}
        sidebarGroups={sidebarGroups}
        activeSidebarPath={activeSidebarPath}
        appName={appName}
      />
    </div>
  );
}

function findSidebarLabel(
  path: string,
  items?: SidebarItem[],
  groups?: SidebarGroup[],
): string | null {
  const allItems: SidebarItem[] = [
    ...(items ?? []),
    ...((groups ?? []).flatMap((g) => g.items)),
  ];
  let best: { label: string; len: number } | null = null;
  for (const item of allItems) {
    if (path === item.to || path.startsWith(item.to + '/')) {
      if (!best || item.to.length > best.len) {
        best = { label: item.label, len: item.to.length };
      }
    }
  }
  return best?.label ?? null;
}
